/**
 * Site Content admin page — manage all banners + arbitrary site-wide content blocks.
 * Two tabs: Banners (existing /api/admin/banners) and Content Blocks (/api/admin/content/blocks).
 */
import { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, FileText, ImageIcon, Layers } from "lucide-react";
import api from "@/lib/api";
import ImageUploader from "@/components/admin/ImageUploader";
import { ExportButtons } from "@/lib/exportData";
import { toast } from "sonner";
import { isLegacyStoreContent } from "@/lib/xplosivContent";

const BANNER_POSITIONS = [
    { value: "hero", label: "Homepage Hero" },
    { value: "event_portfolio", label: "Event Portfolio" },
    { value: "promotion", label: "Event Promotion" },
    { value: "journal", label: "Event Journal" },
];

const BLOCK_GROUPS = ["homepage", "services", "occasions", "products", "footer", "legal", "general"];
const BLOCK_TYPES = ["text", "richtext", "image", "json"];

export default function AdminSiteContent() {
    const [tab, setTab] = useState("banners");

    return (
        <div className="space-y-6" data-testid="admin-site-content">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[#857C72]">
                        Site Content
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Pages &amp; banners</h1>
                </div>
                <nav className="inline-flex p-1 rounded-full bg-[#FAF7F1] border border-[#E7DED2]">
                    {[
                        { key: "banners", label: "Banners", icon: ImageIcon },
                        { key: "blocks", label: "Content blocks", icon: FileText },
                    ].map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`site-content-tab-${t.key}`}
                                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-mono-ui transition-colors ${
                                    active
                                        ? "bg-[#1B1B1F] text-white"
                                        : "text-[#6B645C] hover:bg-white"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" /> {t.label}
                            </button>
                        );
                    })}
                </nav>
            </header>

            {tab === "banners" ? <BannersTab /> : <BlocksTab />}
        </div>
    );
}

// =========================================================
// Banners tab
// =========================================================
function BannersTab() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/banners");
            setBanners((data || []).filter((item) => !isLegacyStoreContent(item)));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const addBlank = () => {
        setBanners([
            ...banners,
            {
                _new: true,
                _tempId: Math.random().toString(36).slice(2),
                position: "hero",
                title: "",
                subtitle: "",
                image: "",
                cta_label: "",
                cta_link: "",
                is_active: true,
                sort_order: banners.length,
            },
        ]);
    };

    const saveOne = async (b) => {
        try {
            if (b._new) {
                const { _new, _tempId, ...payload } = b;
                const { data } = await api.post("/admin/banners", payload);
                setBanners((curr) => curr.map((x) => (x._tempId === _tempId ? data : x)));
                toast.success("Banner created");
            } else {
                const { data } = await api.put(`/admin/banners/${b.id}`, b);
                setBanners((curr) => curr.map((x) => (x.id === b.id ? data : x)));
                toast.success("Banner saved");
            }
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const removeOne = async (b) => {
        if (b._new) {
            setBanners((curr) => curr.filter((x) => x._tempId !== b._tempId));
            return;
        }
        if (!window.confirm("Delete this banner?")) return;
        try {
            await api.delete(`/admin/banners/${b.id}`);
            setBanners((curr) => curr.filter((x) => x.id !== b.id));
            toast.success("Banner deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const update = (idx, patch) => {
        setBanners((curr) => curr.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={addBlank}
                    className="dw-btn-primary inline-flex items-center gap-2 px-4 h-10 font-mono-ui text-xs"
                    data-testid="add-banner-btn"
                >
                    <Plus className="h-4 w-4" /> New banner
                </button>
                <ExportButtons rows={banners} filename="xplosiv_event_banners" columns={["position", "title", "subtitle", "image", "cta_label", "cta_link", "is_active", "sort_order"]} className="ml-auto" />
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading banners…
                </div>
            ) : banners.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-[#E7DED2] p-10 text-center text-[#857C72]">
                    No banners yet. Click <strong>New banner</strong> to create one.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {banners.map((b, idx) => (
                        <article
                            key={b.id || b._tempId}
                            className="rounded-2xl border border-[#E7DED2] bg-white p-4 sm:p-5 space-y-3"
                            data-testid={`banner-row-${idx}`}
                        >
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[10px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72] mb-1">
                                        Position
                                    </label>
                                    <select
                                        value={b.position}
                                        onChange={(e) => update(idx, { position: e.target.value })}
                                        className="w-full px-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    >
                                        {BANNER_POSITIONS.map((p) => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-mono-ui tracking-[0.15em] uppercase text-[#857C72] mb-1">
                                        Sort
                                    </label>
                                    <input
                                        type="number"
                                        value={b.sort_order || 0}
                                        onChange={(e) => update(idx, { sort_order: parseInt(e.target.value || "0", 10) })}
                                        className="w-full px-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    />
                                </div>
                            </div>

                            <input
                                type="text"
                                placeholder="Title"
                                value={b.title || ""}
                                onChange={(e) => update(idx, { title: e.target.value })}
                                className="w-full px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-display"
                            />
                            <textarea
                                placeholder="Subtitle"
                                value={b.subtitle || ""}
                                onChange={(e) => update(idx, { subtitle: e.target.value })}
                                className="w-full px-4 py-2 rounded-2xl bg-[#FFFCF7] border border-[#E7DED2] text-sm min-h-[60px]"
                            />

                            <ImageUploader
                                label="Banner image"
                                value={b.image}
                                onChange={(url) => update(idx, { image: url })}
                            />

                            <div className="grid grid-cols-2 gap-3">
                                <input
                                    type="text"
                                    placeholder="CTA label"
                                    value={b.cta_label || ""}
                                    onChange={(e) => update(idx, { cta_label: e.target.value })}
                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                />
                                <input
                                    type="text"
                                    placeholder="/services or https://…"
                                    value={b.cta_link || ""}
                                    onChange={(e) => update(idx, { cta_link: e.target.value })}
                                    className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                />
                            </div>

                            <label className="inline-flex items-center gap-2 text-xs">
                                <input
                                    type="checkbox"
                                    checked={!!b.is_active}
                                    onChange={(e) => update(idx, { is_active: e.target.checked })}
                                />
                                Active
                            </label>

                            <div className="flex items-center gap-2 pt-2 border-t border-[#E7DED2]">
                                <button
                                    onClick={() => saveOne(b)}
                                    className="dw-btn-primary inline-flex items-center gap-1.5 px-4 h-9 text-xs font-mono-ui"
                                    data-testid={`banner-save-${idx}`}
                                >
                                    <Save className="h-3.5 w-3.5" /> Save
                                </button>
                                <button
                                    onClick={() => removeOne(b)}
                                    className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-xs font-mono-ui"
                                    data-testid={`banner-delete-${idx}`}
                                >
                                    <Trash2 className="h-3.5 w-3.5" /> Delete
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}

// =========================================================
// Content Blocks tab — free-form key/value site content
// =========================================================
function BlocksTab() {
    const [blocks, setBlocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/content/blocks");
            setBlocks((data || []).filter((item) => !isLegacyStoreContent(item)));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const addBlank = () => {
        setBlocks([
            {
                _new: true,
                _tempId: Math.random().toString(36).slice(2),
                key: "",
                label: "",
                value: "",
                type: "text",
                group: "general",
            },
            ...blocks,
        ]);
    };

    const update = (idx, patch) => {
        setBlocks((curr) => curr.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
    };

    const saveOne = async (b) => {
        if (!b.key) {
            toast.error("Key is required (e.g. footer.about)");
            return;
        }
        try {
            const { data } = await api.post("/admin/content/blocks", {
                key: b.key,
                value: b.value,
                type: b.type || "text",
                label: b.label,
                group: b.group || "general",
            });
            setBlocks((curr) => curr.map((x) => (x.key === b.key || x._tempId === b._tempId ? data : x)));
            toast.success("Saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const removeOne = async (b) => {
        if (b._new) {
            setBlocks((curr) => curr.filter((x) => x._tempId !== b._tempId));
            return;
        }
        if (!window.confirm(`Delete content block "${b.key}"?`)) return;
        try {
            await api.delete(`/admin/content/blocks/${encodeURIComponent(b.key)}`);
            setBlocks((curr) => curr.filter((x) => x.key !== b.key));
            toast.success("Deleted");
        } catch {
            toast.error("Delete failed");
        }
    };

    const visible = blocks.filter(
        (b) => !filter || (b.key || "").toLowerCase().includes(filter.toLowerCase()) || (b.label || "").toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    onClick={addBlank}
                    className="dw-btn-primary inline-flex items-center gap-2 px-4 h-10 font-mono-ui text-xs"
                    data-testid="add-block-btn"
                >
                    <Plus className="h-4 w-4" /> New content block
                </button>
                <input
                    type="text"
                    placeholder="Filter by key or label…"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="px-4 py-2 rounded-full bg-white border border-[#E7DED2] text-xs"
                    data-testid="block-filter"
                />
                <ExportButtons rows={blocks} filename="xplosiv_site_content" columns={["key", "label", "group", "type", "value"]} className="ml-auto" />
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-10 text-[#857C72]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading content…
                </div>
            ) : visible.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-[#E7DED2] p-10 text-center text-[#857C72]">
                    No content blocks yet. Examples: <code className="font-mono-ui">homepage.hero_tagline</code>, <code className="font-mono-ui">services.weddings</code>, <code className="font-mono-ui">footer.about</code>.
                </div>
            ) : (
                <div className="space-y-3">
                    {visible.map((b, idx) => {
                        const realIdx = blocks.indexOf(b);
                        return (
                            <article
                                key={b.id || b._tempId}
                                className="rounded-2xl border border-[#E7DED2] bg-white p-4 sm:p-5 space-y-3"
                                data-testid={`block-row-${idx}`}
                            >
                                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                    <input
                                        type="text"
                                        placeholder="Key (e.g. footer.about)"
                                        value={b.key || ""}
                                        onChange={(e) => update(realIdx, { key: e.target.value })}
                                        disabled={!b._new}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm font-mono-ui disabled:opacity-70"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Label"
                                        value={b.label || ""}
                                        onChange={(e) => update(realIdx, { label: e.target.value })}
                                        className="px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    />
                                    <select
                                        value={b.group || "general"}
                                        onChange={(e) => update(realIdx, { group: e.target.value })}
                                        className="px-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    >
                                        {BLOCK_GROUPS.map((g) => (
                                            <option key={g} value={g}>
                                                {g}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        value={b.type || "text"}
                                        onChange={(e) => update(realIdx, { type: e.target.value })}
                                        className="px-3 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    >
                                        {BLOCK_TYPES.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {b.type === "image" ? (
                                    <ImageUploader
                                        compact
                                        value={typeof b.value === "string" ? b.value : ""}
                                        onChange={(url) => update(realIdx, { value: url })}
                                    />
                                ) : b.type === "richtext" ? (
                                    <textarea
                                        rows={4}
                                        value={typeof b.value === "string" ? b.value : ""}
                                        onChange={(e) => update(realIdx, { value: e.target.value })}
                                        placeholder="Rich text value…"
                                        className="w-full px-4 py-2 rounded-2xl bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    />
                                ) : b.type === "json" ? (
                                    <textarea
                                        rows={5}
                                        value={typeof b.value === "string" ? b.value : JSON.stringify(b.value, null, 2)}
                                        onChange={(e) => update(realIdx, { value: e.target.value })}
                                        placeholder='{ "key": "value" }'
                                        className="w-full px-4 py-2 rounded-2xl bg-[#FFFCF7] border border-[#E7DED2] text-sm font-mono-ui"
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        value={typeof b.value === "string" ? b.value : ""}
                                        onChange={(e) => update(realIdx, { value: e.target.value })}
                                        placeholder="Value"
                                        className="w-full px-4 py-2 rounded-full bg-[#FFFCF7] border border-[#E7DED2] text-sm"
                                    />
                                )}

                                <div className="flex items-center gap-2 pt-2 border-t border-[#E7DED2]">
                                    <button
                                        onClick={() => saveOne(b)}
                                        className="dw-btn-primary inline-flex items-center gap-1.5 px-4 h-9 text-xs font-mono-ui"
                                        data-testid={`block-save-${idx}`}
                                    >
                                        <Save className="h-3.5 w-3.5" /> Save
                                    </button>
                                    <button
                                        onClick={() => removeOne(b)}
                                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full bg-white border border-[#E7DED2] hover:bg-red-50 hover:text-red-600 text-xs font-mono-ui"
                                        data-testid={`block-delete-${idx}`}
                                    >
                                        <Trash2 className="h-3.5 w-3.5" /> Delete
                                    </button>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
